/**
 * L'arithmétique des jours du calendrier — **en UTC, du premier au dernier
 * calcul**.
 *
 * Ce n'est pas une précaution de style. Un calendrier qui compose ses dates
 * avec `new Date(annee, mois, jour)` les compose dans le fuseau du poste : à
 * Dakar l'heure locale est UTC, et tout va bien ; sur un poste en `UTC+2`, un
 * `Date` construit à minuit local puis rendu par `toISOString()` recule d'un
 * jour, et l'opérateur lit la journée comptable de la veille sans que rien ne
 * le dise. La faute est silencieuse — le serveur répond, la journée est
 * simplement une autre — et c'est exactement celle que le champ de date existe
 * pour supprimer.
 *
 * Tout ce fichier ne manipule donc que `Date.UTC` et les accesseurs `getUTC*`,
 * et la mise en forme, ailleurs, passe `timeZone: 'UTC'`. Une seule règle, tenue
 * partout.
 *
 * **Le format ISO est le format de sortie.** `AAAA-MM-JJ` est ce que
 * `GET /reconciliation/reports/{date}` prend ; ce que l'opérateur lit est mis
 * en forme dans sa locale, et cette mise en forme ne repart jamais.
 */

/** Le motif d'un jour ISO — quatre chiffres, deux, deux. */
const MOTIF_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Le jour ISO d'un instant, lu en UTC. */
export function toIsoDay(instant: Date): string {
  const annee = String(instant.getUTCFullYear()).padStart(4, '0');
  const mois = String(instant.getUTCMonth() + 1).padStart(2, '0');
  const jour = String(instant.getUTCDate()).padStart(2, '0');
  return `${annee}-${mois}-${jour}`;
}

/**
 * L'instant UTC d'un jour ISO, ou `null` si la chaîne n'en est pas un.
 *
 * `null` et non une date d'aujourd'hui : une valeur illisible qui se
 * remplacerait en silence par le jour courant ferait lire une journée que
 * personne n'a demandée.
 */
export function fromIsoDay(iso: string): Date | null {
  const trouve = MOTIF_ISO.exec(iso);
  if (trouve === null) return null;
  const annee = Number(trouve[1]);
  const mois = Number(trouve[2]);
  const jour = Number(trouve[3]);
  if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return null;
  const instant = new Date(Date.UTC(annee, mois - 1, jour));
  // Le 31 février composerait un 3 mars : la date existe, la chaîne mentait.
  return toIsoDay(instant) === iso ? instant : null;
}

/** Le jour ISO décalé de [jours], en avant ou en arrière. */
export function addDays(iso: string, jours: number): string {
  const instant = fromIsoDay(iso);
  if (instant === null) throw new Error(`ÉCHEC : « ${iso} » n’est pas un jour ISO.`);
  return toIsoDay(new Date(instant.getTime() + jours * 86_400_000));
}

/**
 * Le jour ISO décalé de [mois], le quantième **ramené** au dernier jour du mois
 * d'arrivée quand il n'y existe pas.
 *
 * Le 31 janvier plus un mois vaut le 28 février, jamais le 3 mars : un
 * calendrier qui déborderait ferait sauter un mois entier à qui feuillette avec
 * `Page suivante`.
 */
export function addMonths(iso: string, mois: number): string {
  const instant = fromIsoDay(iso);
  if (instant === null) throw new Error(`ÉCHEC : « ${iso} » n’est pas un jour ISO.`);
  const cible = instant.getUTCMonth() + mois;
  const annee = instant.getUTCFullYear() + Math.floor(cible / 12);
  const normalise = ((cible % 12) + 12) % 12;
  const dernier = new Date(Date.UTC(annee, normalise + 1, 0)).getUTCDate();
  return toIsoDay(
    new Date(Date.UTC(annee, normalise, Math.min(instant.getUTCDate(), dernier))),
  );
}

/**
 * Le premier jour de la semaine de la locale, sur l'échelle ISO 8601 —
 * 1 lundi … 7 dimanche.
 *
 * Lu dans la locale plutôt que décidé ici : `fr` commence le lundi, `en-US` le
 * dimanche, et poser « lundi » en dur serait une règle de plus écrite à la main.
 * Les moteurs qui n'exposent pas `getWeekInfo` retombent sur le lundi, qui est
 * la valeur de la locale du produit — un repli documenté, pas un hasard.
 */
export function firstWeekDay(locale: string): number {
  const avecInfo = new Intl.Locale(locale) as Intl.Locale & {
    getWeekInfo?: () => { firstDay: number };
  };
  try {
    return avecInfo.getWeekInfo?.().firstDay ?? 1;
  } catch {
    return 1;
  }
}

/**
 * Les **six semaines** de la grille d'un mois, chacune de sept jours ISO.
 *
 * Six et non « autant qu'il en faut » : une grille dont la hauteur change au
 * feuilletage fait sauter le panneau sous le curseur, et le jour visé se
 * dérobe. Les jours des mois voisins sont présents — la grille le dit par sa
 * classe, et le lecteur d'écran par le libellé complet de chaque jour.
 */
export function monthGrid(iso: string, premierJour: number): string[][] {
  const instant = fromIsoDay(iso);
  if (instant === null) throw new Error(`ÉCHEC : « ${iso} » n’est pas un jour ISO.`);
  const premierDuMois = new Date(
    Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), 1),
  );
  // `getUTCDay` rend 0 pour dimanche ; l'échelle ISO le met à 7.
  const jourDeSemaine = premierDuMois.getUTCDay() === 0 ? 7 : premierDuMois.getUTCDay();
  const recul = (jourDeSemaine - premierJour + 7) % 7;
  const depart = toIsoDay(new Date(premierDuMois.getTime() - recul * 86_400_000));
  const semaines: string[][] = [];
  for (let semaine = 0; semaine < 6; semaine += 1) {
    const jours: string[] = [];
    for (let jour = 0; jour < 7; jour += 1) {
      jours.push(addDays(depart, semaine * 7 + jour));
    }
    semaines.push(jours);
  }
  return semaines;
}

/** Le mois d'un jour ISO — pour distinguer les jours des mois voisins. */
export function monthOf(iso: string): string {
  const trouve = MOTIF_ISO.exec(iso);
  if (trouve === null) throw new Error(`ÉCHEC : « ${iso} » n’est pas un jour ISO.`);
  return `${trouve[1]}-${trouve[2]}`;
}

/** Le quantième d'un jour ISO, sans zéro de tête. */
export function dayOf(iso: string): number {
  const trouve = MOTIF_ISO.exec(iso);
  if (trouve === null) throw new Error(`ÉCHEC : « ${iso} » n’est pas un jour ISO.`);
  return Number(trouve[3]);
}
